import React from 'react';
import { ArrowLeft, Sparkles, FileText, Download, Save } from 'lucide-react';
import type { SaveStatus } from '../hooks/useAutosave';

interface WorkspaceHeaderProps {
  hasMatchResults: boolean;
  isEnhancing: boolean;
  isGeneratingCover: boolean;
  isGeneratingPdf: boolean;
  saveStatus: SaveStatus;
  onBack: () => void;
  onEnhanceAll: () => void;
  onGenerateCoverLetter: () => void;
  onDownloadPdf: () => void;
}

const statusConfig: Record<SaveStatus, { label: string; className: string }> = {
  idle: { label: 'Saved', className: 'text-zinc-400' },
  saving: { label: 'Saving...', className: 'text-amber-500 animate-pulse' },
  saved: { label: 'Saved', className: 'text-emerald-500' },
  error: { label: 'Save failed', className: 'text-red-500' },
};

const WorkspaceHeader: React.FC<WorkspaceHeaderProps> = ({
  hasMatchResults,
  isEnhancing,
  isGeneratingCover,
  isGeneratingPdf,
  saveStatus,
  onBack,
  onEnhanceAll,
  onGenerateCoverLetter,
  onDownloadPdf,
}) => {
  const status = statusConfig[saveStatus];

  return (
    <div className="flex items-center justify-between gap-3 px-4 sm:px-6 h-14 border-b border-zinc-200 bg-white shrink-0">
      <div className="flex items-center gap-3">
        <button
          className="h-8 px-3 border border-zinc-200 text-zinc-500 hover:text-zinc-700 hover:bg-zinc-50 text-xs font-medium rounded-btn transition-colors inline-flex items-center gap-1.5"
          onClick={onBack}
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Dashboard</span>
        </button>
        <div className="flex items-center gap-1.5 text-xs">
          <Save className={`w-3 h-3 ${status.className}`} />
          <span className={status.className}>{status.label}</span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          className="h-8 px-3 bg-gradient-to-r from-violet-500 to-brand-500 text-white text-xs font-semibold rounded-btn transition-all hover:-translate-y-0.5 hover:shadow-[0_2px_8px_rgba(99,102,241,0.4)] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
          onClick={onEnhanceAll}
          disabled={isEnhancing}
        >
          <Sparkles className="w-3.5 h-3.5" />
          {isEnhancing ? 'Enhancing...' : <span className="hidden sm:inline">Enhance All</span>}
        </button>

        <button
          className="h-8 px-3 border border-zinc-200 text-zinc-500 hover:text-zinc-700 hover:bg-zinc-50 disabled:opacity-30 disabled:cursor-not-allowed text-xs font-medium rounded-btn transition-colors inline-flex items-center gap-1.5"
          onClick={onGenerateCoverLetter}
          disabled={!hasMatchResults || isGeneratingCover}
        >
          <FileText className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">{isGeneratingCover ? 'Generating...' : 'Cover Letter'}</span>
        </button>

        <button
          className="h-8 px-3 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold rounded-btn transition-colors inline-flex items-center gap-1.5"
          onClick={onDownloadPdf}
          disabled={isGeneratingPdf}
        >
          <Download className="w-3.5 h-3.5" />
          {isGeneratingPdf ? '...' : <span className="hidden sm:inline">PDF</span>}
        </button>
      </div>
    </div>
  );
};

export default WorkspaceHeader;
